import { Injectable } from '@angular/core';
import { ColDef } from 'ag-grid-community';
import {
  AccessPermissionService,
  dblclickTooltipDef,
  defaultColumn,
  DELETE_RECORD_LABEL,
  DISTANCE_KM_LABEL,
  grid_widths_map,
} from '@piqet-angular/shared';
import { right } from '@popperjs/core';
import { ProjectsFacade, abbreviateCountryStr } from '@piqet-angular/data';
import { NumericCellEditor } from 'libs/ui/src/lib/numeri-cell-editor/numeric-cell-editor.component';
import { cloneDeep } from 'lodash';
import { SwalService } from 'libs/shared/src/lib/service/swal.service';
import { Subject, take } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ProductIngredientDataGridService {
  productIngredientPayload: any;
  event: any;

  constructor(
    private projectsFacade: ProjectsFacade,
    private swalService: SwalService,
    private accessService: AccessPermissionService
  ) {}

  editProductSub = new Subject<[any, any]>();
  editProductSubData$ = this.editProductSub.asObservable();

  editPopupOpen = new Subject<[any]>();
  editPopupOpenData$ = this.editPopupOpen.asObservable();

  editProductSubManualDistanceSub = new Subject<any>();
  editProductSubManualDistanceSub$ = this.editProductSubManualDistanceSub.asObservable();

  checkDeleteRowProduct = new Subject<[any, any, boolean]>();
  checkDeleteRowProductData$ = this.checkDeleteRowProduct.asObservable();
  allTransportLocationRecords: any;

  checkIngredinetDelete = new Subject<boolean>();
  checkIngredinetDeleteData$ = this.checkIngredinetDelete.asObservable();

  saveProjectManualDistanceIngredient = new Subject<[any, any]>();
  saveProjectManualDistanceIngredientData$ =
    this.saveProjectManualDistanceIngredient.asObservable();

  clearInputFieldProjectManualDistanceIngredient = new Subject<any>();
  clearInputFieldProjectManualDistanceIngredientData$ =
    this.clearInputFieldProjectManualDistanceIngredient.asObservable();

  deleteProdUseRow = new Subject<[any]>();
  deleteProdUseRowObs$ = this.deleteProdUseRow.asObservable();

  editProductManufacture = new Subject<any>();
  editProductManufactureSub$ = this.editProductManufacture.asObservable();

  deleteProductManufacture = new Subject<any>();
  deleteProductManufactureSub$ = this.deleteProductManufacture.asObservable();

  productIngredientDataColumn(): ColDef[] {
    const colDef: ColDef[] = [];

    colDef.push({
      ...defaultColumn,
      colId: 'IngredientName',
      headerName: 'Ingredient name',
      field: 'ingredientName',
      suppressColumnsToolPanel: true,
      cellClass: 'my-permission-cursor-pointer',
      cellStyle: { 'text-decoration': 'underline' },
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      resizable: false,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      cellRenderer: (params: any) => {
        const cellValue = params.value;
        const cellElement = document.createElement('span');
        cellElement.style.cursor = 'pointer';
        cellElement.innerHTML = `${cellValue}`;
        cellElement.title = dblclickTooltipDef;
        cellElement.addEventListener('dblclick', () => {
          this.editPopupOpen.next(params.data?.ingredientId);
        });
        return cellElement;
      },
    });

    colDef.push({
      ...defaultColumn,
      colId: 'IngredientWeightUnit',
      headerName: 'Ingredient weight',
      field: 'ingredientWeightUnit',
      suppressColumnsToolPanel: true,
      cellClass: 'my-permission-cursor-pointer',
      lockVisible: true,
      resizable: false,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      wrapText: true,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'Transport',
      headerName: 'Transportation to manufacturing',
      field: 'transport',
      cellRenderer: 'iconsOfTransport',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'TotalDistance',
      headerName: 'Packaging movement (km)',
      field: 'totalDistance',
      suppressColumnsToolPanel: true,
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      lockVisible: true,
      resizable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'CountryName',
      headerName: 'Country',
      field: 'countryName',
      suppressColumnsToolPanel: true,
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      resizable: false,
      valueFormatter: (params: any) => abbreviateCountryStr(params.data?.countryName),
    });

    colDef.push({
      ...defaultColumn,
      colId: 'SupplierName',
      headerName: 'Supplier of ingredient',
      field: 'supplierName',
      suppressColumnsToolPanel: true,
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      resizable: false,
    });

    this.accessService.hasPermission('edit', '') &&
      colDef.push({
        ...defaultColumn,
        colId: 'actions',
        headerName: 'Actions',
        field: 'actions',
        sortable: false,
        maxWidth: grid_widths_map.sm_column_xs,
        suppressColumnsToolPanel: true,
        cellRenderer: 'actionRenderer',
        cellRendererParams: {
          showDelete: false,
          showEdit: true,
          editData: (data: any, pathName: any) => {
            this.editPopupOpen.next(data?.ingredientId);
          },
        },
        lockVisible: true,
        lockPosition: right,
        resizable: false,
      });
    return colDef;
  }

  getPayload(searchData: any, sortColumnState?: any, event?: any) {
    if (event) {
      this.event = event;
    }
    const currentPage = this.event?.goToPage
      ? this.event.goToPage
      : this.event?.next
      ? this.event.currentPage + 1
      : this.event?.prev
      ? this.event?.currentPage - 1
      : this.event?.gridApi?.paginationGetCurrentPage() + 1;
    const pageSize = this.event?.gridApi.paginationGetPageSize();
    const startCount = pageSize * (currentPage - 1);

    const columns = this.productIngredientDataColumn()
      .map((item: any) => {
        return {
          searchable: true,
          name: item.colId,
          orderable: true,
          data: item.colId,
        };
      })
      .filter(value => value?.name != 'Actions');

    return (this.productIngredientPayload = {
      search: {
        columnNameList: [],
        value: searchData || '',
      },
      columns: columns,
      order: [
        {
          columnName: sortColumnState?.columnName || 'IngredientName',
          dir:
            sortColumnState?.dir[0].toUpperCase() + sortColumnState?.dir.substr(1).toLowerCase() ||
            'Asc',
        },
      ],
      start: startCount || 0,
      length: pageSize == Number(this.event?.totalRecord) ? Number('-1') : pageSize || 10,
      draw: currentPage || 1,
    });
  }

  newProductIngredientColumns(): ColDef[] {
    let enableEditing: any;
    let resetData: any;
    let newValueOfIngredientName: any;
    let newValueOfSupplierName: any;
    let unitName: any;
    let ingredientItemId: any;
    let ingredientSupplierId: any;
    let countryName: any;
    let countryId: any;
    const colDef: ColDef[] = [];
    colDef.push({
      ...defaultColumn,
      colId: 'IngredientName',
      headerName: 'Ingredient',
      field: 'ingredientName',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: true,
      sortable: false,
      cellEditor: 'GridCellRendererComponent',
      cellEditorParams: {
        from: 'ingredientName',
        editData: (data: any) => {
          newValueOfIngredientName = data.ingredientName;
          ingredientItemId = data.ingredientItemId;
        },
      },
      cellRenderer: (params: any) => {
        return `<div class="truncated-text-input-quality" title="${params.data.ingredientName}">${
          params.data.ingredientName ? params.data.ingredientName : ''
        }</div>`;
      },
      valueSetter: (params: any) => {
        const newVal = !!newValueOfIngredientName ? newValueOfIngredientName : 'Select process';
        const valueChanged =
          !!newValueOfIngredientName && !resetData
            ? params.data[params.colDef.field] !== newVal
            : false;
        params.data = { ...params.data };
        if (valueChanged) {
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
          params.data['ingredientItemId'] = ingredientItemId;
          params.data['enableEditing'] = enableEditing;
          const colId = params.colDef.field;
          const rowId = params.node.rowIndex;
          this.projectsFacade.checkUpdateActionIngredient$
            .pipe(take(1))
            .subscribe((flag: boolean) => {
              flag &&
                this.projectsFacade.proudctIngredientProcess(params.data, 'edit', colId, rowId);
            });

          newValueOfIngredientName = '';
          ingredientItemId = '';
        }
        return valueChanged;
      },
      valueGetter: params => {
        return params.data.ingredientName;
      },
    });

    colDef.push({
      ...defaultColumn,
      colId: 'IngredientWeight',
      headerName: 'Weight',
      field: 'ingredientWeight',
      suppressColumnsToolPanel: true,
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: true,
      sortable: false,
      cellEditorSelector: (params: any) => {
        if (params.colDef.colId === 'IngredientWeight') {
          return {
            component: NumericCellEditor,
          };
        }
        return undefined;
      },
      cellEditorParams: {
        from: 'ingredient-weight',
      },
      valueSetter: (params: any) => {
        const newVal = params.newValue;
        const valueChanged = !resetData && params.data[params.colDef.field] !== newVal;
        params.data = { ...params.data };
        if (valueChanged) {
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
          params.data['enableEditing'] = enableEditing;
        }
        const colId = params.colDef.colId;
        const rowId = params.node.rowIndex;
        this.projectsFacade.checkUpdateActionIngredient$
          .pipe(take(1))
          .subscribe((flag: boolean) => {
            flag && this.projectsFacade.proudctIngredientProcess(params.data, 'edit', colId, rowId);
          });
        return false;
      },
    });

    colDef.push({
      ...defaultColumn,
      colId: 'UnitName',
      headerName: 'Unit',
      field: 'unitName',
      suppressColumnsToolPanel: true,
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: true,
      sortable: false,
      cellEditor: 'GridCellRendererComponent',
      cellEditorParams: {
        from: 'unitName',
        editData: (data: any) => {
          unitName = data.unitName;
        },
      },
      cellRenderer: (params: any) => {
        return `<div class="truncated-text" title="${params.data.unitName}">${
          params.data.unitName ? params.data.unitName : ''
        }</div>`;
      },
      valueSetter: (params: any) => {
        const newVal = !!unitName ? unitName : 'Select process';
        const valueChanged =
          !!unitName && !resetData ? params.data[params.colDef.field] !== newVal : false;
        params.data = { ...params.data };
        if (valueChanged) {
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
          params.data['unitName'] = unitName;
          params.data['enableEditing'] = enableEditing;
          const colId = params.colDef.field;
          const rowId = params.node.rowIndex;
          this.projectsFacade.checkUpdateActionIngredient$
            .pipe(take(1))
            .subscribe((flag: boolean) => {
              flag &&
                this.projectsFacade.proudctIngredientProcess(params.data, 'edit', colId, rowId);
            });
          unitName = '';
        }
        return valueChanged;
      },
      valueGetter: params => {
        return params.data.unitName;
      },
    });

    colDef.push({
      ...defaultColumn,
      colId: 'CountryName',
      headerName: 'Country',
      field: 'countryName',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: true,
      sortable: false,
      cellEditor: 'GridCellRendererComponent',
      cellEditorParams: {
        from: 'ingredientCountry',
        editData: (data: any) => {
          countryName = data.countryName;
          countryId = data.countryId;
        },
      },
      cellRenderer: (params: any) => {
        return `<div class="truncated-text" title="${abbreviateCountryStr(
          params.data?.countryName
        )}">${params.data.countryName ? abbreviateCountryStr(params.data?.countryName) : ''}</div>`;
      },
      valueSetter: (params: any) => {
        const newVal = !!countryName ? countryName : 'Select process';
        const valueChanged =
          !!countryName && !resetData ? params.data[params.colDef.field] !== newVal : false;
        params.data = { ...params.data };
        if (valueChanged) {
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
          params.data['countryName'] = countryName;
          params.data['countryId'] = countryId;
          params.data['enableEditing'] = enableEditing;
          const colId = params.colDef.field;
          const rowId = params.node.rowIndex;
          this.projectsFacade.checkUpdateActionIngredient$
            .pipe(take(1))
            .subscribe((flag: boolean) => {
              flag &&
                this.projectsFacade.proudctIngredientProcess(params.data, 'edit', colId, rowId);
            });

          countryName = '';
          countryId = '';
        }
        return valueChanged;
      },
      valueGetter: params => {
        return abbreviateCountryStr(params.data?.countryName);
      },
    });

    colDef.push({
      ...defaultColumn,
      colId: 'SupplierName',
      headerName: 'Supplier',
      field: 'supplierName',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: true,
      sortable: false,
      cellEditor: 'GridCellRendererComponent',
      cellEditorParams: {
        from: 'supplierName',
        editData: (data: any) => {
          newValueOfSupplierName = data.supplierName;
          ingredientSupplierId = data.ingredientSupplierId;
        },
      },
      cellRenderer: (params: any) => {
        return `<div class="truncated-text-input-quality" title="${params.data.supplierName}">${
          params.data.supplierName ? params.data.supplierName : ''
        }</div>`;
      },
      valueSetter: (params: any) => {
        const newVal = !!newValueOfSupplierName ? newValueOfSupplierName : 'Select supplier';
        const valueChanged =
          !!newValueOfSupplierName && !resetData
            ? params.data[params.colDef.field] !== newVal
            : false;
        params.data = { ...params.data };
        if (valueChanged) {
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
          params.data['ingredientSupplierId'] = ingredientSupplierId;
          params.data['supplierName'] = newValueOfSupplierName;
          params.data['enableEditing'] = enableEditing;
          const colId = params.colDef.field;
          const rowId = params.node.rowIndex;
          this.projectsFacade.checkUpdateActionIngredient$
            .pipe(take(1))
            .subscribe((flag: boolean) => {
              flag &&
                this.projectsFacade.proudctIngredientProcess(params.data, 'edit', colId, rowId);
            });

          newValueOfSupplierName = '';
          ingredientSupplierId = '';
        }
        return valueChanged;
      },
      valueGetter: params => {
        return params.data.supplierName;
      },
    });

    this.accessPermission() &&
      colDef.push({
        ...defaultColumn,
        colId: 'actions',
        headerName: 'Actions',
        field: 'actions',
        sortable: false,
        maxWidth: grid_widths_map.sm_column_xs,
        cellRenderer: 'inlineActionButtonComponent',
        cellRendererParams: {
          showInlineEdit: true,
          showInlineDelete: true,
          from: 'productIngredientMain',
          inlineEditData: (data: any) => {
            enableEditing = data.data.enableEditing;
            resetData = data.data.resetData;
          },
          deleteInlineGrid: (data: any) => {
            this.alertConfirmation(data);
          },
        },
        lockVisible: true,
        autoHeaderHeight: true,
        lockPosition: right,
        resizable: false,
      });
    return colDef;
  }

  accessPermission(): boolean {
    return this.accessService.hasPermission('edit', '');
  }

  newProductIngredientTransportColumns(): ColDef[] {
    const colDef: ColDef[] = [];
    colDef.push({
      ...defaultColumn,
      colId: 'IngredientName',
      headerName: 'Ingredient',
      field: 'ingredientName',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'Transport',
      headerName: 'Transport',
      field: 'transport',
      cellRenderer: 'iconsOfTransport',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'Distance',
      headerName: DISTANCE_KM_LABEL,
      field: 'distance',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'actions',
      headerName: 'Actions',
      field: 'actions',
      sortable: false,
      maxWidth: grid_widths_map.sm_column_xs,
      cellRenderer: 'actionRenderer',
      cellRendererParams: {
        showDelete: true,
        deleteData: (data: any) => {
          this.alertConfirmationForIngredientTransport(data);
        },
      },
      lockVisible: true,
      autoHeaderHeight: true,
      lockPosition: right,
      resizable: false,
    });
    return colDef;
  }

  newProductIngredientManactureColumnsForCost(from?: any): ColDef[] {
    const colDef: ColDef[] = [];
    colDef.push({
      ...defaultColumn,
      colId: 'FillingProcessName',
      headerName: 'Process',
      field: 'fillingProcessName',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'Amount',
      headerName: 'Amount',
      field: 'amount',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'CostPerUnit',
      headerName: 'Cost unit',
      field: 'costPerUnit',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'CostPerPackage',
      headerName: 'Cost package',
      field: 'costPerPackage',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'actions',
      headerName: 'Actions',
      field: 'actions',
      sortable: false,
      maxWidth: grid_widths_map.sm_column_xs,
      cellRenderer: 'actionRenderer',
      cellRendererParams: {
        showDelete: true,
        showEdit: true,
        deleteData: (data: any) => {
          from ? this.deleteProdUseData(data) : this.alertConfirmationForManufacture(data);
        },
        editData: (data: any) => {
          this.editProductManufacture.next({ data, isCostOn: true });
        },
      },
      lockVisible: true,
      autoHeaderHeight: true,
      lockPosition: right,
      resizable: false,
    });
    return colDef;
  }
  newProductIngredientManactureColumns(from?: any): ColDef[] {
    const colDef: ColDef[] = [];
    colDef.push({
      ...defaultColumn,
      colId: 'FillingProcessName',
      headerName: 'Process',
      field: 'fillingProcessName',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'Amount',
      headerName: 'Amount',
      field: 'amount',
      wrapText: true,
      autoHeight: true,
      lockVisible: false,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      sortable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'actions',
      headerName: 'Actions',
      field: 'actions',
      sortable: false,
      maxWidth: grid_widths_map.sm_column_xs,
      cellRenderer: 'actionRenderer',
      cellRendererParams: {
        showDelete: true,
        showEdit: true,
        deleteData: (data: any) => {
          from ? this.deleteProdUseData(data) : this.alertConfirmationForManufacture(data);
        },
        editData: (data: any) => {
          this.editProductManufacture.next({ data, isCostOn: false });
        },
      },
      lockVisible: true,
      autoHeaderHeight: true,
      lockPosition: right,
      resizable: false,
    });
    return colDef;
  }

  getTransportModalForManualLocationDataColumn(): ColDef[] {
    const colDef: ColDef[] = [];
    colDef.push({
      ...defaultColumn,
      colId: 'OriginName',
      headerName: 'Location',
      field: 'originName',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
    });
    colDef.push({
      ...defaultColumn,
      colId: 'TransportModeName',
      headerName: 'Transport mode',
      field: 'transportModeName',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
    });
    colDef.push({
      ...defaultColumn,
      colId: 'PersanteageMassLoad',
      headerName: 'Load efficiency',
      field: 'persanteageMassLoad',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
      hide: true,
    });
    colDef.push({
      ...defaultColumn,
      colId: 'Distance',
      headerName: DISTANCE_KM_LABEL,
      field: 'distance',
      cellClass: 'my-permission-cursor-pointer',
      cellEditorSelector: (params: any) => {
        if (params.colDef.colId === 'Distance') {
          return {
            component: NumericCellEditor,
          };
        }
        return undefined;
      },
      cellEditorParams: {
        isCheckTransportDistance: true,
      },
      cellStyle: params => {
        const isCellEditable = params.rowIndex == 0 ? false : true;
        return {
          'pointer-events': isCellEditable ? '' : 'none',
        };
      },
      valueSetter: (params: any) => {
        const oldValue = params.data[params.colDef.field];
        const getDisType = oldValue.substring(oldValue.indexOf(' ') + 1);
        const newVal = params.newValue + ' ' + (getDisType == '0' ? ' ' : getDisType);
        const valueChanged = params.data[params.colDef.field] !== newVal;
        params = cloneDeep(params);
        params = cloneDeep(params);
        if (valueChanged) {
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
        }
        if (!this.isEmpty(newVal) && valueChanged) {
          this.projectsFacade.addLocationProcessIngredient(params.data, 'edit');
          this.editProductSub.next([params.data, false]);
          return valueChanged;
        } else {
          return oldValue;
        }
      },
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'actions',
      headerName: 'Actions',
      field: 'actions',
      sortable: false,
      maxWidth: grid_widths_map.sm_column_xs,
      suppressColumnsToolPanel: true,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'inlineActionButtonComponent',
      cellRendererParams: {
        showInlineEdit: true,
        showInlineDelete: true,
        from: 'projectPages',
        deleteInlineGrid: (data: any) => {
          this.alertConfirmationForManualLocation(data);
        },
        editDataInline: (data: any, pathName: any) => {
          this.projectsFacade.addLocationProcessIngredient(data, 'edit');
          this.editProductSub.next([data, true]);
        },
        saveDataInline: (data: any) => {
          this.saveProjectManualDistanceIngredient.next([data, 'manualLocation']);
        },
        clearInputField: (data: any) => {
          this.clearInputFieldProjectManualDistanceIngredient.next(data);
        },
      },
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      resizable: false,
    });
    return colDef;
  }

  alertConfirmation(data: any) {
    this.swalService.confirm('Are you sure?', DELETE_RECORD_LABEL).then(confirmed => {
      if (confirmed) {
        this.projectsFacade.proudctIngredientProcess(data, 'delete');
        this.checkIngredinetDelete.next(true);
      }
    });
  }

  getTransportModalForManualDestinationDataColumn(): ColDef[] {
    const colDef: ColDef[] = [];

    colDef.push({
      ...defaultColumn,
      colId: 'TransportModeName',
      headerName: 'Transport mode',
      field: 'transportModeName',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
    });
    colDef.push({
      ...defaultColumn,
      colId: 'PersanteageMassLoad',
      headerName: 'Load efficiency',
      field: 'persanteageMassLoad',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
      hide: true,
    });
    colDef.push({
      ...defaultColumn,
      colId: 'Distance',
      headerName: DISTANCE_KM_LABEL,
      field: 'distance',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false,
      editable: false,
      sortable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'actions',
      headerName: 'Actions',
      field: 'actions',
      sortable: false,
      maxWidth: grid_widths_map.sm_column_xs,
      suppressColumnsToolPanel: true,
      cellClass: 'my-permission-cursor-pointer',
      cellRenderer: 'inlineActionButtonComponent',
      cellRendererParams: {
        showInlineEdit: true,
        showInlineDelete: true,
        from: 'projectPages',
        deleteInlineGrid: (data: any) => {
          this.alertConfirmationForManualDistance(data);
        },
        editDataInline: (data: any, pathName: any) => {
          this.projectsFacade.addDistanceTransportIngredeint(data, 'edit');
          this.editProductSubManualDistanceSub.next(data);
        },
        saveDataInline: (data: any) => {
          this.saveProjectManualDistanceIngredient.next([data, 'manualDistance']);
        },
        clearInputField: (data: any) => {
          this.clearInputFieldProjectManualDistanceIngredient.next(data);
        },
      },
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      resizable: false,
    });
    return colDef;
  }

  alertConfirmationForManualLocation(data: any) {
    this.swalService.confirm('Are you sure?', DELETE_RECORD_LABEL).then(confirmed => {
      if (confirmed) {
        this.projectsFacade.locationProcessData$.pipe(take(1)).subscribe((records: any) => {
          this.allTransportLocationRecords = records;
        });
        this.projectsFacade.locationProcessActions(data, 'delete');
        this.checkDeleteRowProduct.next([this.allTransportLocationRecords, data, true]);
      }
    });
  }

  alertConfirmationForManualDistance(data: any) {
    this.swalService.confirm('Are you sure?', DELETE_RECORD_LABEL).then(confirmed => {
      if (confirmed) {
        this.projectsFacade.distanceTransportActions(data, 'delete');
        this.checkDeleteRowProduct.next([undefined, data, false]);
      }
    });
  }

  alertConfirmationForIngredientTransport(data: any) {
    this.swalService.confirm('Are you sure?', DELETE_RECORD_LABEL).then(confirmed => {
      if (confirmed) {
        this.projectsFacade.proudctIngredientTransportProcess(data, 'delete');
      }
    });
  }

  alertConfirmationForManufacture(data: any) {
    this.swalService.confirm('Are you sure?', DELETE_RECORD_LABEL).then(confirmed => {
      if (confirmed) {
        this.deleteProductManufacture.next(data);
      }
    });
  }

  deleteProdUseData(data: any) {
    this.swalService.confirm('Are you sure?', DELETE_RECORD_LABEL).then(confirmed => {
      if (confirmed) {
        this.deleteProdUseRow.next(data);
      }
    });
  }

  isEmpty(val: any) {
    if (val !== undefined || val !== null || val !== '') {
      val = val.trim();
    }
    return val === undefined || val === null || val === '';
  }
}
